import { useEffect, useRef, useState } from 'react';
import { dungeonService } from '../../services/DungeonService';
import { useGameStore } from '../../store/gameSlice';
import { tileHeight } from '../../data/tileset.config';
import { KeyDisplay } from './KeyDisplay';
import { LOCATION_REGISTRY } from '../../data/locations.config';
import {
  INDEPENDENT_LOOT_POOLS,
  WEIGHTED_LOOT_POOLS,
} from '../../data/loot-pools.config';
import { ITEMS } from '../../data/inventory.config';
import { useInventoryStore } from '../../store/inventorySlice';
import { TalentEffects } from '../../services/TalentEffectsService';
import './LocationPrompt.scss';

const LOCATION_LABELS: Record<string, string> = {
  'stairs-down': 'Descend',
  chest: 'Open',
  urn: 'Break',
  'return-sanctuary': 'Return',
  'rest-spring': 'Drink',
  'boost-altar': 'Offer',
  'grimoire-shrine': 'Take',
  merchant: 'Trade',
  'risk-pact': 'Accept',
};

interface LootPreviewEntry {
  itemId: string;
  label: string;
  chance: number;
}

interface InteractableInfo {
  label: string;
  preview: LootPreviewEntry[] | null;
  locked: boolean;
}

function isLocationLocked(locationId: string): boolean {
  if (!LOCATION_REGISTRY[locationId]?.requiresKey) {
    return false;
  }
  return !useInventoryStore
    .getState()
    .slots.some((s) => s?.itemId === 'key' && s.quantity > 0);
}

function getLocationLootPreview(locationId: string): LootPreviewEntry[] | null {
  const def = LOCATION_REGISTRY[locationId];
  if (!def?.effects) {
    return null;
  }
  const poolId = def.effects.find(
    (e) =>
      e.handler === 'spawnItemsFromPool' || e.handler === 'spawnWeightedPool',
  )?.params?.pool;
  if (typeof poolId !== 'string') {
    return null;
  }

  // System 1 pool: per-entry chance.
  const independent = INDEPENDENT_LOOT_POOLS[poolId];
  if (independent && independent.length > 0) {
    return independent
      .slice()
      .sort((a, b) => b.chance - a.chance)
      .map((entry) => ({
        itemId: entry.itemId,
        label: ITEMS[entry.itemId]?.label ?? entry.itemId,
        chance: entry.chance,
      }));
  }

  // System 2 pool: show the per-draw share (weight / total) as an indicative %.
  const weighted = WEIGHTED_LOOT_POOLS[poolId];
  if (weighted && weighted.entries.length > 0) {
    const totalWeight = weighted.entries.reduce((s, e) => s + e.weight, 0);
    return weighted.entries
      .slice()
      .sort((a, b) => b.weight - a.weight)
      .map((entry) => ({
        itemId: entry.itemId,
        label: ITEMS[entry.itemId]?.label ?? entry.itemId,
        chance: totalWeight > 0 ? entry.weight / totalWeight : 0,
      }));
  }

  return null;
}

// Position follows the player via direct DOM mutation in a rAF: no per-frame
// React re-render. React only re-renders when the player enters/leaves an
// interactable zone (label change).
export const LocationPrompt = () => {
  const isPaused = useGameStore((s) => s.isPaused);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [info, setInfo] = useState<InteractableInfo | null>(null);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let raf = 0;
    let lastLocationId: string | null = null;

    const loop = () => {
      const player = dungeonService.getPlayer();
      const location = dungeonService.locationService.getInteractableLocation();
      const curId = location?.id ?? null;

      if (curId !== lastLocationId) {
        lastLocationId = curId;
        if (curId) {
          const preview = TalentEffects.hasOeilDeSage()
            ? getLocationLootPreview(curId)
            : null;
          setInfo({
            label: LOCATION_LABELS[curId] ?? 'Interact',
            preview,
            locked: isLocationLocked(curId),
          });
        } else {
          setInfo(null);
        }
      }

      const el = elementRef.current;
      if (el && player && location) {
        const { cameraPosition, screenWidth, screenHeight, zoomFactor } =
          useGameStore.getState();
        const wx = player.absoluteX;
        const wy = player.absoluteY + tileHeight * 1.2;
        const sx = screenWidth / 2 + (wx - cameraPosition.x) * zoomFactor;
        const sy = screenHeight / 2 - (wy - cameraPosition.y) * zoomFactor;
        el.style.left = `${sx}px`;
        el.style.top = `${sy}px`;
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [isPaused]);

  if (isPaused || !info) {
    return null;
  }

  return (
    <div
      ref={elementRef}
      className="location-prompt"
      style={{ transform: 'translate(-50%, -100%)' }}
    >
      <div className="location-prompt-row">
        {info.locked ? (
          <span className="location-prompt-label">Verrouillé</span>
        ) : (
          <>
            <KeyDisplay context="dungeon" action="INTERACT" size="md" />
            <span className="location-prompt-label">{info.label}</span>
          </>
        )}
      </div>
      {!info.locked && info.preview && info.preview.length > 0 && (
        <ul className="location-prompt-preview">
          {info.preview.map((entry) => (
            <li key={entry.itemId} className="location-prompt-preview-entry">
              <span className="location-prompt-preview-item">
                {entry.label}
              </span>
              <span className="location-prompt-preview-chance">
                {Math.round(entry.chance * 100)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
